import React from 'react';
import { connect } from 'react-redux';
import Downshift from 'downshift';
import { useLocale } from 'locale';
import {
  Action,
  Actions,
  Avatar,
  Separator,
  UserActionsMenu,
  UserActionsWrapper,
  UserInfo,
  AgentConsoleHomeLinkWrapper,
  CountWrapper,
  BetaWrapper,
} from './styled';
import { SmallText, Text } from 'ds/components/typography';
import { Link } from 'components/link';
import { DecodedRouteParams, Route } from 'config/routes';
import { IUserProfileFields, userProfileFieldsSelector } from '../profileFields';
import withEvents from 'analytics/withEvents';
import { State } from 'store/state';
import { desktopUserActionsSelector, actionToIcon, UserAction, UserActionName, UserActionEventData } from '../userActions';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { isAgentConsoleUserSelector, isInactiveAgentSelector } from 'store/state/domainData/selectors';
import { NewLabelV2, SavedListingsCount } from '../more-actions-menu/styled';


interface ActionLinkProps {
  actionName: UserActionName;
  routeName: Route;
  routeParams: Partial<DecodedRouteParams>;
  count?: number;
  onClick: (e: React.MouseEvent) => void;
  isBeta?: boolean;
  isNew?: boolean;
  eventData?: UserActionEventData;
}

const ActionLink: React.FC<ActionLinkProps> = ({ actionName, routeName, routeParams, count, onClick, isBeta, isNew }) => {
  const { t } = useLocale();
  return (
    <Action onClick={onClick}>
      <Link data-auto={`action-${routeName}`} routeName={routeName} routeParams={routeParams} preserveParams>
        {actionToIcon[actionName]}
        <SmallText>{t('navigation.header.actions', { action: actionName, isAgent: false, isProfileMobileMenu: false })}</SmallText>
        {isBeta ? <BetaWrapper>{t('beta')}</BetaWrapper> : null}
        {isNew ? <NewLabelV2>{t('navigation.header.new')}</NewLabelV2> : null}
      </Link>
      {count ? (
        <CountWrapper>
          <SavedListingsCount>{count}</SavedListingsCount>
        </CountWrapper>
      ) : null}
    </Action>
  );
};

interface UserActionsProps extends UserActionsOwnProps, IUserProfileFields {
  onMenuClick?: () => void;
  onClickEvent?: (data: UserActionEventData) => void;
  orderedActions?: UserAction[];
  isAgentConsoleUser: boolean;
  isInactiveAgent: boolean;
}

interface UserActionsOwnProps {
  onLogout: () => void;
}

const UserActionsUnwrapped: React.FC<UserActionsProps> = (props) => {
  const {
    orderedActions,
    onMenuClick,
    firstName,
    lastName,
    initials,
    onLogout,
    isAgentConsoleUser,
    isInactiveAgent,
    avatar,
    email,
    onClickEvent,
  } = props;
  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const isMiddleScreen = screenBreakpoint === 3;

  return (
    <Downshift>
      {({ toggleMenu, isOpen, getLabelProps }) => {
        const onAvatarClick = () => {
          toggleMenu();
          onMenuClick();
        };
        return (
          <div>
            <UserActionsWrapper isMiddleScreen={isMiddleScreen}>
              {initials ? (
                <Avatar
                  data-auto="user-menu"
                  userInitials={initials}
                  imageURL={avatar}
                  {...getLabelProps()}
                  onClick={onAvatarClick}
                />
              ) : null}
              {isOpen ? (
                <UserActionsMenu data-auto="user-action-menu">
                  <UserInfo data-auto="user-info">
                    <Text weight="bold">{firstName} {lastName}</Text>
                    <SmallText data-auto="user-email">{email}</SmallText>
                    {isAgentConsoleUser || isInactiveAgent ? (
                      <AgentConsoleHomeLinkWrapper>
                        <ActionLink
                          key={UserActionName.AgentConsoleHome}
                          actionName={UserActionName.AgentConsoleHome}
                          routeName={Route.AgentConsoleHomePage}
                          routeParams={{}}
                          onClick={toggleMenu as (e: React.MouseEvent) => void}
                        />
                      </AgentConsoleHomeLinkWrapper>
                    ) : null}
                  </UserInfo>
                  <Actions>
                    {orderedActions.map(action => action.customRoute ? (
                      <Action key={action.name}>
                        <a target="_blank" href={action.customRoute} onClick={action.eventData ? () => onClickEvent(action.eventData) : undefined}>
                          {actionToIcon[action.name]}
                          <SmallText style={{ whiteSpace: 'nowrap' }}>{t('navigation.header.actions', { action: action.name, isProfileMobileMenu: false })}</SmallText>
                          {action.isBeta ? <BetaWrapper>{t('beta')}</BetaWrapper> : null}
                          {action.isNew ? (
                            <NewLabelV2 weight="bold" small>
                              {t('navigation.header.new')}
                            </NewLabelV2>
                          ) : null}
                        </a>
                        {action.count ? (
                          <CountWrapper>
                            <SavedListingsCount>{action.count}</SavedListingsCount>
                          </CountWrapper>
                        ) : null}
                      </Action>
                    ) : (
                      <ActionLink
                        key={action.name}
                        actionName={action.name}
                        routeName={action.route}
                        routeParams={action.params}
                        count={action.count}
                        onClick={toggleMenu as (e: React.MouseEvent) => void}
                        isBeta={action.isBeta}
                      />
                    ))}
                    <Separator />
                    <Action onClick={onLogout}>
                      {actionToIcon[UserActionName.Logout]}
                      <SmallText data-auto="action-log-out">{t('navigation.header.actions', { action: UserActionName.Logout })}</SmallText>
                    </Action>
                  </Actions>
                </UserActionsMenu>
              ) : null}
            </UserActionsWrapper>
          </div>
        );
      }}
    </Downshift>
  );
};

const UserActionsUnwrappedWithEvents = withEvents<UserActionsProps>(sendEvent => ({
  onMenuClick() {
    sendEvent('page_navigation_bar_icon_click', 'page', { event: { icon: 'personal' } });
  },
  onClickEvent(data: UserActionEventData) {
    sendEvent(data.name, data.category, { event: data.event });
  },
}))(UserActionsUnwrapped);

const mapStateToProps = (state: State) => ({
  profile: userProfileFieldsSelector(state),
  userActions: desktopUserActionsSelector(state),
  isAgentConsoleUser: isAgentConsoleUserSelector(state),
  isInactiveAgent: isInactiveAgentSelector(state),
});

export const UserActions = connect(mapStateToProps, null, (stateProps, _, ownProps: UserActionsOwnProps) => ({
  ...stateProps.profile,
  isAgentConsoleUser: stateProps.isAgentConsoleUser,
  isInactiveAgent: stateProps.isInactiveAgent,
  orderedActions: stateProps.userActions,
  ...ownProps,
}))(UserActionsUnwrappedWithEvents);
